import { CommandDefinition, CommandSchema } from '../interfaces/commandSchema';
import { humanizeEnum } from './humanizeEnum';
import { isNil } from './isNil';

const extractCommandType = (key: string): string => {
  const match = key.match(/\.(\w+)Payload/);
  return match ? match[1] : '';
};

const commandTypeToId = (commandType: string): string => {
  return commandType
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1_$2')
    .toLowerCase();
};

export const parseCommandSchema = (
  schema: any,
): {
  commandSchemas: Record<string, CommandSchema>;
  commandDefinitions: CommandDefinition[];
} => {
  const commandSchemas: Record<string, CommandSchema> = {};
  const commandDefinitions: CommandDefinition[] = [];

  const defs = schema.$defs || {};

  Object.entries(defs).forEach(([key, def]) => {
    if (key.includes('Payload')) {
      const commandType = extractCommandType(key);
      if (commandType) {
        const commandId = commandTypeToId(commandType);

        // Create a copy of the schema to modify
        const commandSchema = { ...(def as CommandSchema) };

        // Ensure required array exists
        if (!commandSchema.required) {
          commandSchema.required = [];
        }

        // Auto-add array fields to required if not already there
        Object.entries(commandSchema.properties || {}).forEach(([propName, propDef]) => {
          if (propDef.type === 'array' && !commandSchema.required.includes(propName)) {
            commandSchema.required.push(propName);
          }
        });

        commandSchemas[commandId] = commandSchema;

        commandDefinitions.push({
          id: commandId,
          name: humanizeEnum(commandType),
          description: (def as any).description || `Send ${commandType} command to device`,
          schema: commandSchema,
        });
      }
    }
  });

  return { commandSchemas, commandDefinitions };
};

export const getCommandSchema = (commandSchemas: Record<string, CommandSchema>, commandId: string): CommandSchema | undefined => {
  return commandSchemas[commandId];
};

export const generateDefaultPayload = (commandSchemas: Record<string, CommandSchema>, commandId: string): Record<string, any> => {
  const schema = commandSchemas[commandId];
  if (!schema) return {};

  const commandPayload: Record<string, any> = {};

  Object.entries(schema.properties).forEach(([propName, propDef]) => {
    switch (propDef.type) {
      case 'integer':
        commandPayload[propName] = propName === 'unix' ? Math.floor(Date.now() / 1000) : propDef.minimum || 0;
        break;
      case 'string':
        commandPayload[propName] = '';
        break;
      case 'array':
        commandPayload[propName] = [];
        break;
      default:
        commandPayload[propName] = null;
    }
  });

  return {
    device_ids: [],
    payload: {
      context: {
        timeoutSec: 0,
      },
      [commandId]: commandPayload,
    },
    options: {
      command_timeout_sec: 0,
    },
  };
};

export const validatePayload = (commandSchemas: Record<string, CommandSchema>, commandId: string, payload: any): string[] => {
  const schema = commandSchemas[commandId];
  if (!schema) {
    return ['Unknown command'];
  }

  if (!payload.payload || !payload.payload[commandId]) {
    return ['Invalid payload structure'];
  }

  const commandPayload = payload.payload[commandId];

  const isEmpty = (value: any): boolean => {
    if (isNil(value)) {
      return true;
    }
    if (value === '') {
      return true;
    }
    if (Array.isArray(value) && value.length === 0) {
      return true;
    }
    return false;
  };

  const errors = (schema.required || [])
    .filter((field) => isEmpty(commandPayload[field]))
    .map((field) => {
      const fieldDef = schema.properties[field];
      const fieldLabel = fieldDef?.title || field;
      if (fieldDef?.type === 'array') {
        return `At least one ${fieldLabel} is required`;
      }
      return `Field "${fieldLabel}" is required`;
    });

  if (!payload.payload.context || payload.payload.context.timeoutSec === undefined) {
    errors.push('Context with timeoutSec is required');
  }

  if (!Array.isArray(payload.device_ids) || payload.device_ids.length === 0) {
    errors.push('At least one device ID is required');
  }

  return errors;
};
